/**
 * Companion thread sync — IndexedDB stays the hot cache and single source of
 * immediate truth; the backend (`/v1/companion/threads` on edge-companion-api)
 * is the cross-device copy.
 *
 * Design (per the 2026-08-28 companion redesign plan):
 *  - **The dirty flag IS the queue.** Every local save marks the thread
 *    `dirty: true`; `schedulePush` debounces ~2s, then `flushDirtyThreads`
 *    PUTs each dirty thread and clears the flag on success. A failed push
 *    just leaves the flag set, so the next flush retries. Offline,
 *    server-down, auth and routes-not-deployed failures say nothing about
 *    one thread, so they stop the flush; a thread the server rejects as
 *    invalid (400, 413 or 422) is skipped instead, so it cannot block the
 *    threads behind it.
 *  - **LWW on updatedAt.** The server enforces it in SQL; `applied: false`
 *    means our push was stale, so we pull the server copy into IDB (the
 *    in-memory conversation is left alone — the next rehydrate shows it).
 *  - **Pull on history open.** `pullThreads` merges the remote index into
 *    the local list (dedupe by id, newest wins for display, local dirty
 *    preserved). Remote-only threads are fetched lazily on select
 *    (`fetchRemoteThread`).
 *  - **Tombstones** (bounded localStorage list) stop a pull from
 *    resurrecting a thread deleted while offline.
 *  - **Mock auth = no sync** — same skip as the usage query.
 */
import { companionClient } from '@/services/api/api-client'
import { ApiError } from '@/services/api/errors'
import { IS_MOCK_AUTH } from '@/context/plan-constraint-utils'
import {
  getThread,
  saveThread,
  deleteThread,
  getThreadIndex,
  type StoredThread,
  type ThreadIndexEntry,
} from '@/stores/conversation-idb'
import {
  PutThreadResponseSchema,
  ThreadListResponseSchema,
  ThreadWireSchema,
  type ThreadWire,
  type ThreadWireIndexEntry,
} from '@/types/schemas/companion-threads'

const PUSH_DEBOUNCE_MS = 2_000
const TOMBSTONE_KEY = 'edge:companion:thread-tombstones'
const MAX_TOMBSTONES = 50
/**
 * Statuses where the server refused this one thread's payload (bad request,
 * too large, unprocessable): re-sending it cannot succeed, yet it says nothing
 * about the other threads, so a flush skips it instead of stopping.
 */
const REJECTED_THREAD_STATUSES = new Set([400, 413, 422])

function syncEnabled(): boolean {
  return !IS_MOCK_AUTH
}

// ── Tombstones ──────────────────────────────────────────────

function readTombstones(): string[] {
  try {
    const raw = localStorage.getItem(TOMBSTONE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

function writeTombstones(ids: string[]): void {
  try {
    localStorage.setItem(TOMBSTONE_KEY, JSON.stringify(ids.slice(-MAX_TOMBSTONES)))
  } catch {
    /* ignore quota / unavailable storage */
  }
}

// ── Push ────────────────────────────────────────────────────

let pushTimer: ReturnType<typeof setTimeout> | null = null
let flushInFlight = false
let flushListenerInstalled = false

function wireBody(thread: StoredThread) {
  return {
    title: thread.title,
    page: thread.page,
    contextKey: thread.originContextKey,
    messages: thread.messages,
    createdAt: new Date(thread.createdAt).toISOString(),
    updatedAt: new Date(thread.updatedAt).toISOString(),
  }
}

function threadFromWire(wire: ThreadWire): StoredThread {
  const updatedAt = Date.parse(wire.updatedAt)
  return {
    id: wire.threadId,
    title: wire.title ?? 'Untitled',
    messages: wire.messages,
    page: wire.page ?? 'general',
    originContextKey: wire.contextKey ?? wire.page ?? 'general',
    createdAt: Date.parse(wire.createdAt),
    updatedAt,
    dirty: false,
    syncedAt: Date.now(),
    remoteUpdatedAt: updatedAt,
  }
}

async function pushThread(entry: ThreadIndexEntry): Promise<void> {
  const thread = await getThread(entry.id)
  if (!thread || !thread.dirty) return
  const raw = await companionClient.put<unknown>(
    `/v1/companion/threads/${thread.id}`,
    wireBody(thread),
  )
  const parsed = PutThreadResponseSchema.safeParse(raw)
  if (!parsed.success) throw new Error('Unexpected thread-sync response')

  if (parsed.data.applied) {
    // Guard against the autosave race: if a NEW message landed while the PUT
    // was in flight, the IDB copy is newer than the snapshot we pushed —
    // leave it dirty for the next flush instead of clobbering it with the
    // stale snapshot.
    const latest = await getThread(thread.id)
    if (latest && latest.updatedAt !== thread.updatedAt) return
    await saveThread({
      ...thread,
      dirty: false,
      syncedAt: Date.now(),
      remoteUpdatedAt: parsed.data.serverUpdatedAt
        ? Date.parse(parsed.data.serverUpdatedAt)
        : thread.updatedAt,
    })
    return
  }
  // Stale push — the server copy is newer (another device). LWW: pull it
  // into IDB; the in-memory conversation is untouched.
  const remote = await fetchRemoteThread(thread.id)
  if (!remote) {
    // Could not pull — keep ours dirty so a later flush retries.
    return
  }
}

/**
 * Drain every dirty thread, sequentially. Safe to call repeatedly; failures
 * leave the dirty flags in place for the next flush. A thread the server
 * rejects as invalid (400, 413 or 422) is skipped and the drain carries on;
 * any other failure stops it.
 */
export async function flushDirtyThreads(): Promise<void> {
  if (!syncEnabled() || flushInFlight) return
  flushInFlight = true
  try {
    const index = await getThreadIndex()
    for (const entry of index.filter((e) => e.dirty)) {
      try {
        await pushThread(entry)
      } catch (err) {
        // The server judged this one thread invalid: skip it and keep going.
        // Its flag stays dirty, so the next flush retries it.
        if (err instanceof ApiError && REJECTED_THREAD_STATUSES.has(err.status)) continue
        // Anything else (offline, server-down, auth, routes absent, conflict)
        // may be transient or hit every thread, so stop. The flags stay dirty
        // and the next flush retries.
        break
      }
    }
  } finally {
    flushInFlight = false
  }
}

/** Debounced push — call after every local thread save. */
export function schedulePush(): void {
  if (!syncEnabled()) return
  if (!flushListenerInstalled) {
    flushListenerInstalled = true
    // Flush pending work when the tab hides (best effort).
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void flushDirtyThreads()
    })
  }
  if (pushTimer) clearTimeout(pushTimer)
  pushTimer = setTimeout(() => {
    pushTimer = null
    void flushDirtyThreads()
  }, PUSH_DEBOUNCE_MS)
}

// ── Pull ────────────────────────────────────────────────────

/** GET one remote thread and cache it locally (clean). Null on any failure. */
export async function fetchRemoteThread(id: string): Promise<StoredThread | null> {
  if (!syncEnabled()) return null
  try {
    const raw = await companionClient.get<unknown>(`/v1/companion/threads/${id}`)
    const parsed = ThreadWireSchema.safeParse(raw)
    if (!parsed.success) return null
    const thread = threadFromWire(parsed.data)
    await saveThread(thread)
    return thread
  } catch {
    return null
  }
}

function mergeIndexes(
  local: ThreadIndexEntry[],
  remote: ThreadWireIndexEntry[],
  tombstones: Set<string>,
): ThreadIndexEntry[] {
  const byId = new Map<string, ThreadIndexEntry>()
  for (const entry of local) byId.set(entry.id, entry)
  for (const wire of remote) {
    if (tombstones.has(wire.threadId)) continue
    const existing = byId.get(wire.threadId)
    const remoteUpdatedAt = Date.parse(wire.updatedAt)
    // A locally-dirty thread is never overwritten by pull; otherwise newest
    // wins for display.
    if (existing && (existing.dirty || existing.updatedAt >= remoteUpdatedAt)) continue
    byId.set(wire.threadId, {
      id: wire.threadId,
      title: wire.title ?? 'Untitled',
      page: wire.page ?? 'general',
      updatedAt: remoteUpdatedAt,
      messageCount: wire.messageCount,
      dirty: false,
    })
  }
  return [...byId.values()].sort((a, b) => b.updatedAt - a.updatedAt)
}

/**
 * The history panel's data source: local index merged with the remote list
 * (when reachable). Falls back to the local index alone on any failure.
 */
export async function pullThreads(): Promise<ThreadIndexEntry[]> {
  const local = await getThreadIndex()
  if (!syncEnabled()) return local
  try {
    const raw = await companionClient.get<unknown>('/v1/companion/threads')
    const parsed = ThreadListResponseSchema.safeParse(raw)
    if (!parsed.success) return local
    return mergeIndexes(local, parsed.data.threads, new Set(readTombstones()))
  } catch {
    return local
  }
}

// ── Delete ──────────────────────────────────────────────────

/** Delete locally, then remotely (best effort — tombstoned when offline). */
export async function deleteThreadEverywhere(id: string): Promise<void> {
  await deleteThread(id)
  if (!syncEnabled()) return
  writeTombstones([...readTombstones(), id])
  try {
    await companionClient.delete(`/v1/companion/threads/${id}`)
    writeTombstones(readTombstones().filter((t) => t !== id))
  } catch {
    // Tombstone stays — the pull-merge won't resurrect it, and there is no
    // retry loop for deletes in v1 (worst case: an orphan row server-side).
  }
}
